import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, FormControl, InputLabel, MenuItem, Pagination, Select, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getDashboard } from '../services/AdminService';

const GameDashboard = ({ embedded = false }) => {
    const { t } = useTranslation();
    const [data, setData] = useState({ roomCount: 0, playerCount: 0, connectionCount: 0, activeRoomOperationCount: 0,
        filteredRoomCount: 0, page: 1, pageSize: 24, totalPages: 1, gameKeys: [], dashboardData: [] });
    const [gameFilter, setGameFilter] = useState('all');
    const [modeFilter, setModeFilter] = useState('all');
    const [draftGameFilter, setDraftGameFilter] = useState('all');
    const [draftModeFilter, setDraftModeFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(24);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(false);
        getDashboard({ gameKey: gameFilter, roomMode: modeFilter, page, pageSize }).then(response => {
            if (active && response) setData({ ...response, dashboardData: response.dashboardData || [] });
        }).catch(() => { if (active) setError(true); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [gameFilter, modeFilter, page, pageSize]);

    const gameKeys = useMemo(() => Array.from(new Set(['poker', 'shan9mee', ...(data.gameKeys || [])])), [data.gameKeys]);
    const rooms = data.dashboardData;
    const gameName = key => t(`subscription.${key}`, { defaultValue: key });

    return <div className={embedded ? 'admin-room-summary' : 'app-page dashboard-page'}>
        <div className="dashboard-summary">
            <div className="dashboard-stat"><span>{t('dashboardUi.rooms')}</span><strong>{data.roomCount}</strong></div>
            <div className="dashboard-stat"><span>{t('dashboardUi.players')}</span><strong>{data.playerCount}</strong></div>
            <div className="dashboard-stat"><span>{t('dashboardUi.connections')}</span><strong>{data.connectionCount}</strong></div>
            <div className="dashboard-stat"><span>{t('dashboardUi.activeOperations')}</span><strong>{data.activeRoomOperationCount}</strong></div>
        </div>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{t('dashboardUi.loadFailed')}</Alert>}
        <Box component="form" className="dashboard-room-filters" onSubmit={event => {
            event.preventDefault(); setGameFilter(draftGameFilter); setModeFilter(draftModeFilter); setPage(1);
        }}>
            <FormControl size="small">
                <InputLabel id="dashboard-game-filter-label">{t('dashboardUi.game')}</InputLabel>
                <Select labelId="dashboard-game-filter-label" value={draftGameFilter} label={t('dashboardUi.game')} disabled={loading}
                    onChange={event => setDraftGameFilter(event.target.value)}>
                    <MenuItem value="all">{t('dashboardUi.allGames')}</MenuItem>
                    {gameKeys.map(key => <MenuItem key={key} value={key}>{gameName(key)}</MenuItem>)}
                </Select>
            </FormControl>
            <FormControl size="small">
                <InputLabel id="dashboard-mode-filter-label">{t('dashboardUi.mode')}</InputLabel>
                <Select labelId="dashboard-mode-filter-label" value={draftModeFilter} label={t('dashboardUi.mode')} disabled={loading}
                    onChange={event => setDraftModeFilter(event.target.value)}>
                    <MenuItem value="all">{t('dashboardUi.allModes')}</MenuItem>
                    <MenuItem value="hosted">{t('dashboardUi.hosted')}</MenuItem>
                    <MenuItem value="random">{t('dashboardUi.random')}</MenuItem>
                </Select>
            </FormControl>
            <FormControl size="small">
                <InputLabel id="dashboard-page-size-label">{t('dashboardUi.pageSize')}</InputLabel>
                <Select labelId="dashboard-page-size-label" value={pageSize} label={t('dashboardUi.pageSize')} disabled={loading}
                    onChange={event => { setPageSize(Number(event.target.value)); setPage(1); }}>
                    {[12, 24, 48, 100].map(size => <MenuItem key={size} value={size}>{size}</MenuItem>)}
                </Select>
            </FormControl>
            <Button type="submit" variant="contained" disabled={loading}>{t('dashboardUi.applyFilters')}</Button>
            <Typography variant="body2" color="text.secondary">{t('dashboardUi.filteredRooms', { count: data.filteredRoomCount })}</Typography>
        </Box>

        {!loading && rooms.length === 0 ? <Typography className="dashboard-empty" color="text.secondary">{t('dashboardUi.noRooms')}</Typography> :
            <Box className="dashboard-room-grid">
                {rooms.map(room => {
                    const players = room.players?.length ? room.players : (room.playersName || []).map(playerName => ({ playerName, isJoined: true, playerState: '' }));
                    return <Card key={`${room.gameKey}:${room.roomId}`} className="dashboard-room-card" variant="outlined">
                        <CardContent>
                            <Box className="dashboard-room-heading">
                                <Typography component="h3" variant="subtitle1">{gameName(room.gameKey)} · {t(`dashboardUi.${room.roomMode || 'hosted'}`)}</Typography>
                                <Chip size="small" label={room.gameState || t('dashboardUi.unknownState')} />
                            </Box>
                            <Typography variant="body2"><strong>{t('dashboardUi.roomId')}:</strong> {room.roomId}</Typography>
                            <Box className="dashboard-player-info">
                                <Typography variant="body2" component="h4"><strong>{t('dashboardUi.players')} ({players.length})</strong></Typography>
                                <Box className="dashboard-player-list">
                                    {players.length ? players.map((player, index) =>
                                        <Chip key={`${player.playerName}:${index}`} size="small" variant={player.isJoined ? 'filled' : 'outlined'}
                                            color={player.isJoined ? 'primary' : 'default'}
                                            label={`${player.playerName} · ${t(player.isJoined ? 'dashboardUi.joined' : 'dashboardUi.absent')}${player.playerState ? ` · ${player.playerState}` : ''}`} />)
                                        : <Typography variant="caption" color="text.secondary">{t('dashboardUi.noPlayers')}</Typography>}
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>;
                })}
            </Box>}
        {data.totalPages > 1 && <Pagination className="dashboard-room-pagination" count={data.totalPages} page={data.page || page}
            disabled={loading} onChange={(_, value) => setPage(value)} color="primary" />}
    </div>;
};

export default GameDashboard;
